import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { useResource } from "../../hooks/useResource.js";
import { FormShell } from "../../components/forms/FormShell.jsx";
import { FormSection } from "../../components/forms/FormSection.jsx";
import { Input } from "../../components/ui/Input.jsx";
import { Textarea } from "../../components/ui/Textarea.jsx";
import { Select } from "../../components/ui/Select.jsx";
import { Switch } from "../../components/ui/Switch.jsx";
import { Button } from "../../components/ui/Button.jsx";
import { PageLoader } from "../../components/ui/Spinner.jsx";
import { ImageUpload } from "../../components/upload/ImageUpload.jsx";

const DEFAULTS = {
  customerName: "",
  rating: 5,
  source: "direct",
  text: "",
  photo: "",
  isFeatured: false,
  sortOrder: 0,
};

function buildPayload(values) {
  const out = { ...values };
  if (out.rating !== "") out.rating = Number(out.rating);
  if (out.sortOrder !== "") out.sortOrder = Number(out.sortOrder);
  return out;
}

export default function TestimonialFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = id === "new" || !id;

  const { detail, create, update } = useResource("testimonials", { toastLabel: "testimonial" });
  const { data: existing, isLoading } = detail(isNew ? null : id);

  const form = useForm({ defaultValues: DEFAULTS });
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = form;

  useEffect(() => {
    if (existing) reset({ ...DEFAULTS, ...existing });
  }, [existing, reset]);

  useEffect(() => {
    document.title = isNew ? "New Testimonial · Admin" : "Edit Testimonial · Admin";
  }, [isNew]);

  const onSubmit = handleSubmit(async (values) => {
    const payload = buildPayload(values);
    if (isNew) {
      await new Promise((resolve, reject) => {
        create.mutate(payload, {
          onSuccess: (created) => {
            navigate(`/testimonials/${created._id}`);
            resolve();
          },
          onError: reject,
        });
      });
    } else {
      await new Promise((resolve, reject) => {
        update.mutate({ id, data: payload }, { onSuccess: resolve, onError: reject });
      });
    }
  });

  if (!isNew && isLoading) return <PageLoader />;

  return (
    <FormShell
      title={isNew ? "New Testimonial" : `Edit ${existing?.customerName ?? ""}'s review`}
      backTo="/testimonials"
      backLabel="Back to testimonials"
      onSubmit={onSubmit}
      primaryAction={
        <Button type="submit" loading={isSubmitting}>
          {isNew ? "Create testimonial" : "Save changes"}
        </Button>
      }
      secondaryAction={
        <Button type="button" variant="secondary" onClick={() => navigate("/testimonials")}>
          Cancel
        </Button>
      }
      sidebar={
        <>
          <FormSection title="Display">
            <Controller
              control={control}
              name="isFeatured"
              render={({ field }) => (
                <Switch
                  label="Show on homepage marquee"
                  hint="Featured testimonials appear in the rotating slider."
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
            <Input type="number" label="Sort order" {...register("sortOrder")} />
          </FormSection>

          <FormSection title="Customer photo">
            <Controller
              control={control}
              name="photo"
              render={({ field }) => (
                <ImageUpload
                  value={field.value}
                  onChange={field.onChange}
                  subdir="team"
                  aspect="square"
                  hint="Optional. When blank, a gold initials avatar is shown."
                />
              )}
            />
          </FormSection>
        </>
      }
    >
      <FormSection title="Basics">
        <Input
          label="Customer name"
          required
          error={errors.customerName?.message}
          {...register("customerName", { required: "Customer name is required" })}
        />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Input
            type="number"
            label="Rating (1–5)"
            min="1"
            max="5"
            required
            error={errors.rating?.message}
            {...register("rating", {
              required: "Rating is required",
              min: { value: 1, message: "Min 1" },
              max: { value: 5, message: "Max 5" },
            })}
          />
          <Select label="Source" {...register("source")}>
            <option value="google">Google</option>
            <option value="productreview">ProductReview</option>
            <option value="direct">Direct</option>
          </Select>
        </div>
        <Textarea
          label="Testimonial text"
          required
          rows={5}
          error={errors.text?.message}
          {...register("text", { required: "Testimonial text is required" })}
          hint="What the customer said. Shown verbatim on the homepage and Awards section."
        />
      </FormSection>
    </FormShell>
  );
}
